export interface IMadIdState {
    previewSection: IPreviewSectionMad;
    projectInfoSection: IProjectInfoSectionMad;
    problemSection: IProblemSectionMad;
    solutionSection: ISolutionSectionMad;
    accordionSection: IAccordionSectionMad;
    featuresSection: IFeaturesSectionMad;
    benefitsSection: IBenefitsSectionMad;
    quoteSection: IQuoteSectionMad;
}

export interface IPreviewSectionMad {
    title: string;
    subtitle: string;
    image: string;
    text: string[];
}

export interface IProjectInfoSectionMad {
    title: string;
    list: IListMad[];
}

export interface IProblemSectionMad {
    title: string;
    text: string;
}

export interface ISolutionSectionMad{
    title: string;
    text: string[];
}

export interface IAccordionSectionMad {
    title: string;
    subtitle: string;
    accordion: IAccordionMad[];
}

export interface IFeaturesSectionMad {
    title: string;
    image: string;
    features: IFeaturesMad[];
}

export interface IAccordionMad {
    id: number;
    title: string;
    text: Array<string>;
}

export interface IFeaturesMad {
    icon: string;
    iconAlt: string;
    text: string;
}

export interface IListMad {
    title: string;
    subtitle: string;
}

export interface IBenefitsSectionMad{
    title: string;
    image: string;
    benefits: IBenefitsMad[];
}

export interface IBenefitsMad{
    icon: string;
    iconAlt: string;
    text: string;
}

export interface IQuoteSectionMad {
    comment: string;
    author: IAuthorMad;
}

export interface IAuthorMad{
    name: string;
    avatar: string;
    specialist: string;
}

export const MadIdState: IMadIdState = {
    previewSection: {
        title: 'MAD ID',
        subtitle: `Education platform for creatives`,
        image: '/mad-id-images/preview.png',
        text: [
            `MAD ID helps creatives advance their craft and career through more collaborative and efficient learning experiences. It delivers the most relevant real-world education in a quick-to-consume format.`,
            `Agilie Team developed MAD ID’s platform in order to give designers the opportunity to expand their skills and grow professionally through daily case studies and lessons from top designers.`
        ]
    },
    projectInfoSection: {
        title: 'Project info',
        list: [
            {
                title: 'Project:',
                subtitle: 'Educational platform for designers'
            },
            {
                title: 'Product:',
                subtitle: 'Web application'
            },
            {
                title: 'Category:',
                subtitle: 'Educational online platform'
            },
            {
                title: 'Project timeline:',
                subtitle: '9 months'
            },
        ]
    },
    problemSection: {
        title: 'Problem',
        text: 'Users of existing learning systems face several barriers. First, most of them are busy - they have families, jobs, and other commitments - and find it difficult to learn in large chunks of time through lengthy courses. Our client MAD ID decided to provide a solution based on daily, asynchronous learning and thereby solve the time problem. Second, users currently study mainly through reading and listening, which have an extremely low level of knowledge retention. MAD ID wanted to make things right and provide a much better learning outcome.'
    },
    solutionSection: {
        title: 'Our Solution',
        text: [
            `Our goal was to solve the barriers of time, isolation, and quality that most designers face in their current learning solutions. So we created a fully-customized, flexible education platform.`,
            `MAD allows designers to “make time” for skills development daily in a collaborative manner and learn incrementally without having to make huge schedule commitments.`,
            `Our team started from scratch and built the platform using Ruby on Rails. We were performing the heavy lifting in collaboration with the U.S.- based designer.`
        ]
    },
    accordionSection: {
        title: 'Development Challenges',
        subtitle: ``,
        accordion: [
            {
                id: 1,
                title: 'Delivery feature',
                text: [
                    `The product consists of two main components: the production of educational content and its subsequent delivery. Our team has been working on the content delivery feature. The result is a Netflix-like platform with the ability to customize educational path and curriculum. This means that the system is focused on providing users with a personalized learning experience.`
                ]
            },
            {
                id: 2,
                title: 'Time management solution',
                text: [
                    `One of the main tasks set by our client was effective time management. We had to help people with busy schedules get time to study no matter what.`,
                    `In the end, we have developed a solution offering users a modular approach so that they could take the courses in any convinient order.`
                ]
            },
            {
                id: 3,
                title: 'Filters for asynchronous learning',
                text: [
                    `With a Netflix-like UI, users are able to filter different types of content and select specific lessons to add to their personalized educational path. As a result, everyone's learning experience is unique. It's a novel approach to education that is typically carried out in a very synchronous manner.`
                ]
            },
            {
                id: 4, 
                title: `Video content processing`, 
                text: [
                    `The basis of the MAD ID platform is video content, which allows users to take full advantage of collaborative learning mechanisms. Therefore, these videos should be large and high quality.`,
                    `To deliver the best user experince, we decided to connect with Vimeo, a third-party video sharing service. Vimeo processes the videos and transfers them to the MAD ID platform. This significantly reduces browser loading and improves the playback quality of the volume stream playback.`
                ]
            },
            {
                id: 5, 
                title: `Scheduling the lesson submission process`, 
                text: [
                    `The client wanted to personalize the lesson submission process: according to his idea, users should receive new assignments from their customized learning plan on the days they choose. To make this happen, we have broken our curriculum into bite-sized pieces that users consume on their own schedule. They can focus on the content they need and leave out irrelevant lessons.`,
                    `Also, this feature required integration with third-party apps and so forth.`
                ]
            },
        ]
    },   
    featuresSection: {
        title: `Features`,
        image: '/mad-id-images/features.png',
        features: [
            {
                icon: '/mad-id-images/icons/video.png',
                iconAlt: 'video icon',
                text: 'Watching video lessons from well-known creatives'
            },
            {
                icon: '/mad-id-images/icons/comment.png',
                iconAlt: 'comment icon',
                text: `Online communication with mentors`
            },
            {
                icon: '/mad-id-images/icons/arrow.png',
                iconAlt: 'arrow icon',
                text: 'Easy & fast video download'
            },
            {
                icon: '/mad-id-images/icons/dashboard.png',
                iconAlt: 'dashboard icon',
                text: 'Full integration with Slack, Stripe, Vimeo, Intercom, and MailChimp'
            },
        ]
    },
    benefitsSection: {
        title: 'Benefits for the global creative community',
        image: '/mad-id-images/benefits.png',
        benefits: [
            {
                icon: '/mad-id-images/icons/rocket.png',
                iconAlt: 'rocket icon',
                text: 'A great chance for both novice and experienced designers to develop new skills'
            },
            {
                icon: '/mad-id-images/icons/trophy.png',
                iconAlt: 'trophy icon',
                text: `The opportunity to learn from the world’s top creatives`
            },
            {
                icon: '/mad-id-images/icons/clock.png',
                iconAlt: 'clock icon',
                text: '24/7 access to the personalized learning content'
            },
            {
                icon: '/mad-id-images/icons/globe.png',
                iconAlt: 'globe icon',
                text: 'the opportunity to communicate with leading companies such as Dropbox and Facebook'
            },
        ]
    },
    quoteSection: {
        comment: `Agilie was great at taking direction and the feedback on user testing has been amazing so far. They’re a cost-effective and responsive team that communicates daily and delivers on time. They have a friendly disposition and are passionate about the work.`,
        author: {
            name: `Pete`,
            avatar: `/mad-id-images/author.png`,
            specialist: `MAD ID, Founder`,
        }
    }
}

